/**
 * PeopleStep — Stage 1 of RAO cart flow.
 *
 * Wires three pieces together:
 *   - GlobalCustomerSearch primitive (unified SSN / CIF / name search) for
 *     finding existing CIFs. Replaces the legacy hand-rolled search on the
 *     RAO PeopleScreen.
 *   - PersonDialog (v0.1.36+) dispatcher for both review-add (hit on an
 *     existing CIF) and new-person capture (no match, officer clicks "+ New
 *     Person"). Routes by entity category: Personal → IndividualPersonDialog;
 *     other categories → CategoryStub until their dialogs land.
 *   - PeopleList for grouped display of the cart's people.
 *
 * Persistence (row-store P2, 2026-07-06): each save issues a per-party
 * command — POST /v3/carts/{cartId}/parties (add) · PATCH …/parties/{partyKey}
 * (update) · DELETE …/parties/{partyKey} (remove) — via
 * client.retail.carts.addParty/updateParty/removeParty. The server upserts the
 * CartParties row, recomposes cart.peopleJson, and returns the fresh CartDto
 * projection, so the old whole-blob PATCH + expectedVersion conflict-retry
 * machinery is gone. The business-entity BO mirror
 * (metadataJson.beneficialOwners) is patched separately after a successful
 * party command (metadata-only update, NO expectedVersion) when it drifts.
 * Emits `cart.people.*` SessionEvents for audit.
 */
import { noTinIdentityKey } from '@/services/rules/requiredIndividualFields';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { CartDto } from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { identifyCart } from '@/utils/cartLabel';
import { Banner, type CustomerSearchRow } from '@/components/primitives';
import { PeopleList } from '../components/PeopleList';
import { PersonSearchBar } from '../components/people/PersonSearchBar';
import { CifCompareModal } from '../components/personDialog/components/people/CifCompareModal';
import { PersonDialog, type PersonDialogMode } from '../components/personDialog/PersonDialog';
import { PdwShell, type NonPersonalPick } from '../components/personDialog/PdwShell';
import { isExistingCustomer } from '../components/personDialog/shared/existingCustomer';
import { useProfileChange, type ProfileChangeNotice } from '@/components/customer/useProfileChange';
import { findGroupForType } from '../components/personDialog/findGroupForType';
import {
  boRequirementFor,
  readBeneficialOwners,
  toBoMirror,
} from '../components/personDialog/business/beneficialOwners';
import { useApi } from '../hooks/useApi';
import { runCustomerSearch, filterByNameAndDob } from '../hooks/usePersonSearch';
import { useEmitEvent } from '../hooks/useEmitEvent';
import { toNamedJsonDate } from '@/utils/jxDates';
import type { RaoPerson, RaoPersonRole } from '../types';

export interface PeopleStepProps {
  cart: CartDto;
  onCartUpdated?: (cart: CartDto) => void;
  readOnly?: boolean;
  /**
   * Open "+ New Person" as soon as the step shows — set when the officer asked to add a
   * joint owner from the product modal and nobody in the cart could be one (2026-10-16).
   * The normal add flow runs (search, duplicate-CIF guard, save).
   */
  openAddPersonOnMount?: boolean;
  /** Called once the add-person dialog has been opened for openAddPersonOnMount. */
  onAddPersonOpened?: () => void;
  /** People shown without Edit / Remove (account maintenance: the account's existing owners, from Core). */
  lockedPersonIds?: ReadonlyArray<string>;
}

/**
 * Parse cart.peopleJson into the rich RaoPerson[] shape. Tolerant of both the
 * Wave-1 minimal shape and the Wave-2 envelope `{version:1, people:[...]}`.
 */
/** Stable per-cart person id. Uses crypto.randomUUID where available, else a
 *  timestamp+random fallback (sufficient for an in-cart unique key). */
/** The cart is gone (purged) or the empty-cart sweep cancelled it (CHG-0110). */
export function isClearedCartError(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.code === 'CART_SWEPT' || err.code === 'CART_NOT_FOUND'
    || (err.httpStatus === 404 && /cart not found/i.test(err.message));
}

function makePersonId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return `person-${c.randomUUID()}`;
  return `person-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * BACKLOG-0721b — derive the cart.metadataJson.beneficialOwners MIRROR from the
 * business-entity people's person.extra.beneficialOwners, so the compliance
 * gates (useWorkflowGates / complianceRulesEngine BO gate / BusinessGates) keep
 * their existing read contract while BO data lives entity-scoped on the person.
 *
 * Returns the new metadataJson string to send, or undefined when the mirror is
 * already in sync (avoids needless metadata writes that could clobber the
 * ProductsStep beneficiaries / CartSidebar notes writers). Read-merges to
 * preserve sibling keys.
 */
function deriveBoMetadata(metadataJson: string | undefined, people: ReadonlyArray<RaoPerson>): string | undefined {
  const mirror = people.flatMap((p) =>
    boRequirementFor(p.customerType).kind === 'none'
      ? []
      : toBoMirror(readBeneficialOwners(p.extra)),
  );
  let meta: Record<string, unknown> = {};
  if (metadataJson) {
    try {
      const parsed = JSON.parse(metadataJson) as unknown;
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        meta = parsed as Record<string, unknown>;
      }
    } catch { /* fall through with empty meta */ }
  }
  const existing = Array.isArray(meta['beneficialOwners']) ? meta['beneficialOwners'] : [];
  if (existing.length === 0 && mirror.length === 0) return undefined;
  if (JSON.stringify(existing) === JSON.stringify(mirror)) return undefined;
  return JSON.stringify({ ...meta, beneficialOwners: mirror });
}

function parsePeople(cart: CartDto): RaoPerson[] {
  if (!cart.peopleJson) return [];
  try {
    const raw = JSON.parse(cart.peopleJson) as unknown;
    let arr: Array<Record<string, unknown>> = [];
    if (Array.isArray(raw)) arr = raw as Array<Record<string, unknown>>;
    else if (raw && typeof raw === 'object' && Array.isArray((raw as { people?: unknown }).people)) {
      arr = (raw as { people: Array<Record<string, unknown>> }).people;
    }
    return arr.map(normalizePerson);
  } catch {
    return [];
  }
}

/** Coerce a loose object to a valid RaoPerson with sane defaults. */
function normalizePerson(p: Record<string, unknown>): RaoPerson {
  const role = (typeof p['role'] === 'string' ? p['role'] : 'primary') as RaoPersonRole;
  const firstName = typeof p['firstName'] === 'string' ? p['firstName'] : '';
  const lastName = typeof p['lastName'] === 'string' ? p['lastName'] : '';
  const fullName = typeof p['fullName'] === 'string'
    ? p['fullName']
    : `${firstName} ${lastName}`.trim() || '(unnamed)';
  return {
    ...(typeof p['id'] === 'string' ? { id: p['id'] } : {}),
    role,
    mode: (p['mode'] === 'new' || p['mode'] === 'existing') ? (p['mode'] as 'new' | 'existing') : (p['cifId'] ? 'existing' : 'new'),
    firstName,
    lastName,
    fullName,
    ...(typeof p['middleName'] === 'string' ? { middleName: p['middleName'] } : {}),
    ...(typeof p['suffix'] === 'string' ? { suffix: p['suffix'] } : {}),
    ...(typeof p['name2'] === 'string' ? { name2: p['name2'] } : {}),
    ...(typeof p['cifId'] === 'string' ? { cifId: p['cifId'] } : {}),
    ...(typeof p['dob'] === 'string' ? { dob: p['dob'] } : {}),
    ...(typeof p['taxId'] === 'string' ? { taxId: p['taxId'] } : {}),
    ...(typeof p['tinCode'] === 'string' ? { tinCode: p['tinCode'] as RaoPerson['tinCode'] } : {}),
    ...(typeof p['customerType'] === 'string' ? { customerType: p['customerType'] as RaoPerson['customerType'] } : {}),
    ...(typeof p['gender'] === 'string' ? { gender: p['gender'] as RaoPerson['gender'] } : {}),
    ...(typeof p['insiderCode'] === 'string' ? { insiderCode: p['insiderCode'] } : {}),
    ...(typeof p['phoneHome'] === 'string' ? { phoneHome: p['phoneHome'] } : {}),
    ...(typeof p['phoneCell'] === 'string' ? { phoneCell: p['phoneCell'] } : {}),
    ...(typeof p['phoneBus'] === 'string' ? { phoneBus: p['phoneBus'] } : {}),
    ...(typeof p['email'] === 'string' ? { email: p['email'] } : {}),
    ...(typeof p['addr'] === 'string' ? { addr: p['addr'] } : {}),
    ...(typeof p['addr2'] === 'string' ? { addr2: p['addr2'] } : {}),
    ...(typeof p['city'] === 'string' ? { city: p['city'] } : {}),
    ...(typeof p['state'] === 'string' ? { state: p['state'] } : {}),
    ...(typeof p['zip'] === 'string' ? { zip: p['zip'] } : {}),
    ...(typeof p['callClass'] === 'string' ? { callClass: p['callClass'] } : {}),
    ...(typeof p['backupWithholding'] === 'boolean' ? { backupWithholding: p['backupWithholding'] } : {}),
    ...(typeof p['isPep'] === 'boolean' ? { isPep: p['isPep'] } : {}),
    ...(typeof p['email2'] === 'string' ? { email2: p['email2'] } : {}),
    // Mailing-address fields (separate from physical addr*)
    ...(typeof p['mailAddr'] === 'string' ? { mailAddr: p['mailAddr'] } : {}),
    ...(typeof p['mailAddr2'] === 'string' ? { mailAddr2: p['mailAddr2'] } : {}),
    ...(typeof p['mailCity'] === 'string' ? { mailCity: p['mailCity'] } : {}),
    ...(typeof p['mailState'] === 'string' ? { mailState: p['mailState'] } : {}),
    ...(typeof p['mailZip'] === 'string' ? { mailZip: p['mailZip'] } : {}),
    ...(typeof p['mailForeign'] === 'boolean' ? { mailForeign: p['mailForeign'] } : {}),
    ...(typeof p['employmentStatus'] === 'string' ? { employmentStatus: p['employmentStatus'] } : {}),
    ...(typeof p['employer'] === 'string' ? { employer: p['employer'] } : {}),
    ...(typeof p['occupation'] === 'string' ? { occupation: p['occupation'] } : {}),
    ...(typeof p['naicsCode'] === 'string' ? { naicsCode: p['naicsCode'] } : {}),
    ...(typeof p['cddExempt'] === 'boolean' ? { cddExempt: p['cddExempt'] } : {}),
    ...(typeof p['cddExemptReasonCode'] === 'string' ? { cddExemptReasonCode: p['cddExemptReasonCode'] } : {}),
    ...(typeof p['cipStatus'] === 'string' ? { cipStatus: p['cipStatus'] as RaoPerson['cipStatus'] } : {}),
    ...(typeof p['amlInsightScore'] === 'number' ? { amlInsightScore: p['amlInsightScore'] } : {}),
    ...(typeof p['amlInsightReportDate'] === 'string' ? { amlInsightReportDate: p['amlInsightReportDate'] } : {}),
    ...(typeof p['amlInsightRunBy'] === 'string' ? { amlInsightRunBy: p['amlInsightRunBy'] } : {}),
    ...(typeof p['citizenshipStatus'] === 'string' ? { citizenshipStatus: p['citizenshipStatus'] as RaoPerson['citizenshipStatus'] } : {}),
    // Alien_CType / NRA capture (Wave NRA-scaffold, 2026-04-27).
    // NOTE (NRA smoke test, 2026-06-12): the W-8 fields (countryOfTaxResidency,
    // alienW8Type, alienLastW8Dt, alienW8ExpDt, alienIRSRptType, irsRecipCode)
    // and the foreignResidence* address fields are RETIRED from the UI
    // (IndividualNraSection) — W-8 is not required for CIP and the foreign
    // residence address was never captured in legacy procedure. The round-trip
    // below is kept INTENTIONALLY as stored-data tolerance so in-flight
    // persons saved with those values don't lose them on load/save.
    ...(typeof p['countryOfTaxResidency'] === 'string' ? { countryOfTaxResidency: p['countryOfTaxResidency'] } : {}),
    ...(typeof p['visaType'] === 'string' ? { visaType: p['visaType'] } : {}),
    ...(typeof p['visaExpDt'] === 'string' ? { visaExpDt: p['visaExpDt'] } : {}),
    ...(typeof p['i94Number'] === 'string' ? { i94Number: p['i94Number'] } : {}),
    ...(typeof p['i94ExpDt'] === 'string' ? { i94ExpDt: p['i94ExpDt'] } : {}),
    ...(typeof p['foreignPassportNumber'] === 'string' ? { foreignPassportNumber: p['foreignPassportNumber'] } : {}),
    ...(typeof p['foreignPassportCountry'] === 'string' ? { foreignPassportCountry: p['foreignPassportCountry'] } : {}),
    ...(typeof p['foreignResidenceAddr'] === 'string' ? { foreignResidenceAddr: p['foreignResidenceAddr'] } : {}),
    ...(typeof p['foreignResidenceCity'] === 'string' ? { foreignResidenceCity: p['foreignResidenceCity'] } : {}),
    ...(typeof p['foreignResidenceState'] === 'string' ? { foreignResidenceState: p['foreignResidenceState'] } : {}),
    ...(typeof p['foreignResidencePostal'] === 'string' ? { foreignResidencePostal: p['foreignResidencePostal'] } : {}),
    ...(typeof p['foreignResidenceCountry'] === 'string' ? { foreignResidenceCountry: p['foreignResidenceCountry'] } : {}),
    ...(typeof p['alienW8Type'] === 'string' ? { alienW8Type: p['alienW8Type'] as RaoPerson['alienW8Type'] } : {}),
    ...(typeof p['alienLastW8Dt'] === 'string' ? { alienLastW8Dt: p['alienLastW8Dt'] } : {}),
    ...(typeof p['alienW8ExpDt'] === 'string' ? { alienW8ExpDt: p['alienW8ExpDt'] } : {}),
    ...(typeof p['alienIRSRptType'] === 'string' ? { alienIRSRptType: p['alienIRSRptType'] as RaoPerson['alienIRSRptType'] } : {}),
    ...(typeof p['irsRecipCode'] === 'string' ? { irsRecipCode: p['irsRecipCode'] } : {}),
    ...(typeof p['irsExmpCode'] === 'string' ? { irsExmpCode: p['irsExmpCode'] } : {}),
    ...(typeof p['fornTaxId'] === 'string' ? { fornTaxId: p['fornTaxId'] } : {}),
    ...(typeof p['countryOfCitizenship'] === 'string' ? { countryOfCitizenship: p['countryOfCitizenship'] } : {}),
    ...(typeof p['relCode'] === 'string' ? { relCode: p['relCode'] } : {}),
    ...(typeof p['dirty'] === 'boolean' ? { dirty: p['dirty'] } : {}),
    // `extra` is the open-ended bucket — carries ofacStatus + any other
    // future fields without schema churn. Round-trip verbatim if it's an
    // object; drop otherwise so malformed payloads can't poison state.
    ...(p['extra'] && typeof p['extra'] === 'object' && !Array.isArray(p['extra'])
      ? { extra: p['extra'] as Record<string, unknown> }
      : {}),
  };
}

/**
 * Build an empty RaoPerson shell for "+ New Person" capture.
 *
 * `customerType` is mandatory now — the caller resolves it via the
 * EntityCategoryPicker *before* this runs so findGroupForType dispatches to
 * the right per-category dialog (Individual today, CategoryStub placeholder
 * for Business / Trust / Estate / Fiduciary / Government until they ship).
 */
function blankPerson(
  role: RaoPersonRole,
  customerType: RaoPerson['customerType'],
  seed?: { citizenshipStatus?: RaoPerson['citizenshipStatus']; stayPurpose?: string },
): RaoPerson {
  // TIN code default — Individual → 'S' (SSN); NRA → 'Forn'; other → 'E' (EIN).
  const tinCode: RaoPerson['tinCode'] =
    customerType === 'NonResidentAlien' ? 'Forn' :
    customerType === 'Individual' ? 'S' : 'E';
  const citizenshipStatus: RaoPerson['citizenshipStatus'] =
    seed?.citizenshipStatus ??
    (customerType === 'NonResidentAlien' ? 'Non-Resident Alien' : 'US Citizen');
  const person: RaoPerson = {
    role,
    mode: 'new',
    firstName: '',
    lastName: '',
    fullName: '',
    customerType,
    tinCode,
    citizenshipStatus,
    cipStatus: 'pending',
  };
  // Stay purpose is captured per the canonical cohort gate (NRA path); stash
  // it on person.extra so IndividualCitizenshipSection can read it without a
  // schema bump to the RaoPerson type.
  if (seed?.stayPurpose) {
    person.extra = { ...(person.extra ?? {}), stayPurpose: seed.stayPurpose };
  }
  return person;
}

export function PeopleStep({
  cart,
  onCartUpdated,
  readOnly = false,
  openAddPersonOnMount = false,
  onAddPersonOpened,
  lockedPersonIds,
}: PeopleStepProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const emit = useEmitEvent();
  const navigate = useNavigate();
  const location = useLocation();
  const cartRecoveredNotice = (location.state as { cartRecoveredNotice?: string } | null)?.cartRecoveredNotice;

  const [people, setPeople] = useState<RaoPerson[]>(() => parsePeople(cart));
  const [saving, setSaving] = useState<boolean>(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // BACKLOG-1675 (Bill 2026-10-02) — an EXISTING customer's edits write to Core
  // Director when the Person Dialog is saved: compare with live Core, confirm the
  // changed fields + the accounts they flow to (checkbox per account), then
  // CustMod + AcctMod per account + 90-day notes. The saved person carries the
  // applied key so the commit pipeline does not repeat the CIF update.
  const { begin: beginProfileChange, modal: profileChangeModal, comparing: comparingProfile } = useProfileChange();
  const [profileNotice, setProfileNotice] = useState<ProfileChangeNotice | null>(null);

  const [modalMode, setModalMode] = useState<PersonDialogMode>('new');
  const [modalPerson, setModalPerson] = useState<RaoPerson | null>(null);

  // no-dup-CIF guard (Bill 2026-06-02, person-fix 4; compare-surface 2026-06-12)
  // — when a fresh "+ New Person" matches an on-file CIF by SSN or name+DOB,
  // hold the append + open the CifCompareModal (Entered vs On-file side-by-side)
  // so the officer decides informed: use the existing CIF (optionally updating
  // contact fields via the change-of-contact ceremony) or continue adding —
  // and BOTH paths preserve every session artifact. Replaces the bare
  // DuplicateCifPrompt. checkingDup = silent CustSrch in flight.
  const [dupGuard, setDupGuard] =
    useState<{
      person: RaoPerson;
      matches: CustomerSearchRow[];
      matchedOn: string;
      /** True when matched ON the entered tax id (forces clear-SSN on not-a-match). */
      matchedOnTaxId: boolean;
      early?: boolean;
      /** Stamps the no-TIN proof on the open draft (early name+DOB check, Bill 2026-10-14). */
      markNoTinCleared?: (key: string) => void;
    } | null>(null);
  const [checkingDup, setCheckingDup] = useState<boolean>(false);
  // E1 (Bill 2026-06-03) — SSN already early-checked this open-modal session, so
  // re-blurring the same SSN doesn't re-prompt. Cleared when a new draft opens.
  const lastEarlyDupSsn = useRef<string | null>(null);

  // Bill 2026-05-26 (v0.14.48, BACKLOG-0070) — "+ New Person" now opens the
  // canonical PdwShell which owns Stages 1-9 end-to-end for the Personal
  // cohort. For non-Personal buckets, the shell still hands off at Stage 3
  // (subtype confirm) via `onNonPersonalDispatch`, which seeds a blank
  // person + opens the legacy per-category PersonDialog. Existing-CIF
  // search-result + Edit flows stay on the legacy dispatcher entirely
  // (PdwShell is the "+ New Person" entry point only).
  const [shellOpen, setShellOpen] = useState<boolean>(false);
  const [pickerRole, setPickerRole] = useState<RaoPersonRole>('primary');
  // Slot-4 T1 — review-ENTRY into PdwShell for Personal review-add / Edit.
  // When set, PdwShell skips Stages 1-4 and opens at the seeded Stage-9 review.
  // Non-Personal review-add / Edit keep the legacy PersonDialog dispatcher
  // (which now renders the converged EntityConvergedShell chrome).
  const [shellEntryPerson, setShellEntryPerson] = useState<RaoPerson | null>(null);
  const [shellEntryMode, setShellEntryMode] = useState<'review-add' | 'edit'>('review-add');
  // Stable id of the person currently being EDITED (set on edit-open, cleared on
  // add-open + after save). Guarantees an edit updates-in-place even if the
  // dialog/shell drops person.id on the round-trip — fixes the edit-duplicates
  // bug for new (pre-CIF) persons. Bill 2026-06-02.
  const [editingPersonId, setEditingPersonId] = useState<string | undefined>(undefined);
  // Re-entrancy guard for onModalSave (Bill 2026-06-03 double-add fix). Holds the
  // identity key of the save currently in flight; a duplicate dispatch bails.
  const inFlightSaveKeyRef = useRef<string | null>(null);
  const isPersonalCohort = (ct: RaoPerson['customerType']): boolean =>
    findGroupForType(ct) === 'personal';

  // Re-sync on parent reload.
  useEffect(() => {
    setPeople(parsePeople(cart));
  }, [cart.cartId, cart.version]);

  const cartCifIds = useMemo(
    () => people.map((p) => p.cifId).filter((id): id is string => !!id),
    [people],
  );

  /**
   * Row-store P2 — BO-mirror sync after a party command (BACKLOG-0721b kept).
   * The party command response carries the freshly recomposed cart; when the
   * business-entity beneficial-owner mirror (metadataJson.beneficialOwners —
   * the gate read contract) is out of sync with the new people set, patch it
   * with a metadata-ONLY cart update WITHOUT expectedVersion (last-write-wins;
   * party commands never take a cart-level version). Returns the freshest
   * cart to hand to onCartUpdated. A mirror-patch failure surfaces as a
   * saveError but the party save itself has already succeeded.
   */
  const syncBoMirror = useCallback(
    async (freshCart: CartDto, signal: AbortSignal): Promise<CartDto> => {
      const boMetadataJson = deriveBoMetadata(freshCart.metadataJson, parsePeople(freshCart));
      if (boMetadataJson === undefined) return freshCart;
      const res = await client.retail.carts.update(
        freshCart.cartId,
        { metadataJson: boMetadataJson },
        { signal },
      );
      if (!res.success || !res.cart) {
        setSaveError(res.error ?? 'Beneficial-owner summary update failed — re-save the person to retry.');
        return freshCart;
      }
      return res.cart;
    },
    [client],
  );

  /**
   * PersonSearchBar onSelectExisting — the wrapper has already run CustInq
   * and handed us a hydrated RaoPerson. We just open the review-add modal.
   */
  const onSearchSelect = useCallback(
    (person: RaoPerson): void => {
      setEditingPersonId(undefined); // review-add of an on-file CIF → dedup by cifId, not a stale edit id
      // Slot-4 T1 — Personal review-add converges onto PdwShell (review-entry).
      // Non-Personal stays on the PersonDialog dispatcher (converged chrome).
      if (isPersonalCohort(person.customerType)) {
        setShellEntryPerson(person);
        setShellEntryMode('review-add');
        setShellOpen(true);
        return;
      }
      setModalPerson(person);
      setModalMode('review-add');
    },
    [],
  );

  // Role assigned to the next added/picked person. First pick = primary;
  // Wave 1 #2.6 — Person.role pre-binning removed (Bill 2026-04-28).
  // Ownership level is per-product on the OwnershipStep matrix; PeopleStep
  // is now a flat list. The pendingRole heuristic + per-tier add buttons
  // (onAddJoint/onAddRelationship) are gone — single onAddNew opens the
  // category picker for every add.
  const pendingRole: RaoPersonRole = 'primary'; // legacy plumbing for PersonSearchBar; cosmetic only

  const onAddNew = useCallback(() => {
    // Bill 2026-05-26 (v0.14.48) — open PdwShell. It owns Stages 1-9 for
    // the Personal cohort end-to-end; non-Personal buckets hand off to
    // the legacy PersonDialog dispatcher at Stage 3 via onNonPersonalDispatch.
    setPickerRole('primary'); // unused downstream; kept for blankPerson signature
    setShellEntryPerson(null); // fresh add → full bucket → cohort → docs flow
    setEditingPersonId(undefined); // fresh add → append (never update-in-place)
    lastEarlyDupSsn.current = null; // E1 — new draft: allow a fresh early dup-check
    setShellOpen(true);
  }, []);

  // Requested from the product modal's "+ Add joint owner" (MacKenzie Mott 2026-10-16).
  useEffect(() => {
    if (!openAddPersonOnMount || readOnly) return;
    onAddNew();
    onAddPersonOpened?.();
  }, [openAddPersonOnMount, readOnly, onAddNew, onAddPersonOpened]);

  /**
   * Non-Personal dispatch handler (BACKLOG-0070). When PdwShell completes
   * Stage 3 subtype confirm for a non-Personal bucket (Business / Trust /
   * Estate / Fiduciary / Government / SpecialPurpose), the shell closes
   * and we open the legacy PersonDialog. Each of those dialogs migrates
   * into the shell in 0071+ batches; until then the dispatch handoff
   * preserves the pre-v0.14.48 behaviour for non-Personal flows so they
   * can't regress while Personal is being moved.
   */
  const onNonPersonalDispatch = useCallback(
    (pick: NonPersonalPick) => {
      setShellOpen(false);
      setEditingPersonId(undefined); // fresh non-Personal add → append
      setModalPerson(blankPerson(pickerRole, pick.customerType));
      setModalMode('new');
    },
    [pickerRole],
  );

  /**
   * Personal-flow terminal save handler. PdwShell calls this when the FSR
   * commits the Stage 9 review form. We close the shell, add the draft to
   * the cart, and persist via the same path as legacy onModalSave.
   */
  const onShellSave = useCallback(
    (saved: RaoPerson): void => {
      setShellOpen(false);
      setShellEntryPerson(null);
      void guardThenSave(saved);
    },
    // guardThenSave declared below; forward reference safe via closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const onEditPerson = useCallback((p: RaoPerson) => {
    // Remember the edit target's stable id so the save updates IN PLACE even if
    // the dialog/shell doesn't round-trip person.id (edit-duplicates fix).
    setEditingPersonId(p.id);
    // Slot-4 T1 — Personal Edit converges onto PdwShell (review-entry, Edit
    // mode); non-Personal Edit stays on the PersonDialog dispatcher.
    if (isPersonalCohort(p.customerType)) {
      setShellEntryPerson(p);
      setShellEntryMode('edit');
      setShellOpen(true);
      return;
    }
    setModalPerson(p);
    setModalMode('edit');
  }, []);

  const onRemovePerson = useCallback(
    async (target: RaoPerson): Promise<void> => {
      // Row-store P2 — DELETE /v3/carts/{cartId}/parties/{partyKey} replaces
      // the whole-blob peopleJson PATCH. partyKey is the person's stable
      // clientId (person.id). Post-backfill every composed person carries an
      // id; an id-less row can't be addressed as a party row, so bail with a
      // reload hint instead of guessing (cifId is NOT a valid partyKey).
      if (!target.id) {
        setSaveError('This person is missing a stable cart id — reload the cart and remove them again.');
        return;
      }
      // Remove ONLY the targeted person locally (optimistic; keyed by stable
      // id — filtering by cifId removed BOTH copies when two rows shared a
      // CIF, the edit-duplicate bug). The server response recomposes the
      // authoritative peopleJson.
      const nextPeople = people.filter((p) => p.id !== target.id);
      setPeople(nextPeople);
      emit({
        eventType: 'cart.people.removed',
        eventCategory: 'cart-lifecycle',
        cartId: cart.cartId,
        payload: { cifId: target.cifId ?? null, fullName: target.fullName },
      });
      setSaving(true);
      setSaveError(null);
      const signal = makeSignal();
      try {
        const res = await client.retail.carts.removeParty(cart.cartId, target.id, { signal });
        if (!res.success || !res.cart) {
          setSaveError(res.error ?? 'Remove failed');
          return;
        }
        // BO mirror: removing a business entity must also drop its owners
        // from metadataJson.beneficialOwners (the gates' read contract).
        onCartUpdated?.(await syncBoMirror(res.cart, signal));
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setSaveError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setSaving(false);
      }
    },
    [cart.cartId, client, emit, makeSignal, onCartUpdated, people, syncBoMirror],
  );

  // CHG-0110 (Bill 2026-10-26, ask #288): the overnight sweep (or another window) can clear an
  // empty application while the officer is still entering the first person. Saving that person
  // then starts a fresh application with the same officer, branch and setup, saves the person to
  // it, moves the editor there and says so, instead of failing with "cart not found".
  const recoverClearedCart = useCallback(
    async (person: RaoPerson, personId: string, signal: AbortSignal): Promise<void> => {
      const origin: string = cart.origin;
      if (origin !== 'fsr-inbranch' && origin !== 'fsr-self') {
        setSaveError('This application is no longer available. Start a new application from New Acct.');
        return;
      }
      try {
        const created = await client.retail.carts.create(
          {
            origin: cart.origin,
            officerCode: cart.officerCode,
            branch: cart.branch,
            ...(cart.metadataJson ? { cartDataJson: cart.metadataJson } : {}),
          },
          { signal },
        );
        const fresh = created.cart;
        if (!created.success || !fresh) throw new Error(created.error ?? 'Could not start a new application.');
        const res = await client.retail.carts.addParty(
          fresh.cartId,
          { clientId: personId, person: person as unknown as Record<string, unknown> },
          { signal },
        );
        if (!res.success || !res.cart) throw new Error(res.error ?? 'Save failed');
        emit({
          eventType: 'cart.recreated-after-clear',
          eventCategory: 'cart-lifecycle',
          cartId: fresh.cartId,
          payload: { previousCartId: cart.cartId },
        });
        const hadProducts = identifyCart(undefined, cart.productsJson).what;
        navigate(`/rao/${fresh.cartId}`, {
          replace: true,
          state: {
            cartRecoveredNotice:
              'This application had been cleared because no one had been added to it yet, so a new one was started with the person you just entered.'
              + (hadProducts ? ' Add the products again.' : ''),
          },
        });
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setSaveError(
          'This application had been cleared, and a new one could not be started: '
          + (err instanceof Error ? err.message : 'unknown error')
          + '. Start a new application from New Acct.',
        );
      }
    },
    [cart.branch, cart.cartId, cart.metadataJson, cart.officerCode, cart.origin, cart.productsJson, client, emit, navigate],
  );

  const onModalSave = useCallback(
    async (saved: RaoPerson): Promise<void> => {
      // (Bill 2026-06-03) — re-entrancy guard against a double-add. onModalSave
      // is reachable from several paths (guardThenSave's branches + the dupGuard
      // effect); if the SAME saved person is dispatched twice in the same tick,
      // the second call's idx lookup runs before setPeople flushes (idx = -1) and
      // appends a duplicate (the "two Catherine CIFs + version mismatch" smoke
      // test). Key on the person's identity; bail if an identical save is still
      // in flight. Reset in finally so a legitimate later re-save proceeds.
      const saveKey = [
        saved.id ?? '',
        saved.cifId ?? '',
        (saved.taxId ?? '').replace(/\D/g, ''),
        saved.fullName ?? '',
        saved.role ?? '',
      ].join('|');
      if (inFlightSaveKeyRef.current === saveKey) return;
      inFlightSaveKeyRef.current = saveKey;
      try {
      // Dedupe: match by stable id first (so editing a NEW person updates in
      // place instead of appending a duplicate — id-less cifId-only dedup used
      // to duplicate new persons on re-save), then by existing CIF; else ADD.
      // Resolve the dedup target: the saved person's own id, else the id we
      // remembered when the edit opened (the dialog/shell may not round-trip it),
      // else match an existing CIF (review-add of an on-file CIF must not append
      // a second copy), else ADD.
      let nextPeople: RaoPerson[];
      const targetId = saved.id ?? editingPersonId;
      const idx = targetId
        ? people.findIndex((p) => p.id === targetId)
        : saved.cifId
          ? people.findIndex((p) => p.cifId === saved.cifId)
          : -1;
      // Stamp a stable per-cart id (Bill 2026-06-01). The AddToCartModal owner-
      // binding keys Primary/Joint/Beneficiary off person.id; without it the
      // owner tile click is a no-op (setPrimary('')) and no cart can commit.
      // Preserve the target id on update; generate one on add.
      const personId = saved.id ?? targetId ?? (idx >= 0 ? people[idx]?.id : undefined) ?? makePersonId();
      // Date hygiene at the single save choke point — dob persists as ISO
      // "YYYY-MM-DD" ONLY (jxDates rule: Core Director rejects other shapes
      // with System Error 1; <input type="date"> renders them blank). An
      // unparseable value passes through so validation can name it.
      const isoDob = saved.dob ? (toNamedJsonDate(saved.dob) || saved.dob) : saved.dob;
      const withId: RaoPerson = saved.id === personId && isoDob === saved.dob
        ? saved
        : { ...saved, id: personId, ...(isoDob !== saved.dob ? { dob: isoDob } : {}) };
      if (idx >= 0) {
        nextPeople = [...people];
        nextPeople[idx] = withId;
      } else {
        nextPeople = [...people, withId];
      }
      setPeople(nextPeople);
      emit({
        eventType: idx >= 0 ? 'cart.people.updated' : 'cart.people.added',
        eventCategory: 'cart-lifecycle',
        cartId: cart.cartId,
        payload: { role: saved.role, cifId: saved.cifId ?? null, mode: saved.mode },
      });
      setModalPerson(null);
      setEditingPersonId(undefined); // edit/add complete → reset target
      // Row-store P2 — per-party command replaces the whole-blob peopleJson
      // PATCH: add → POST /parties (clientId = the stamped person id);
      // update → PATCH /parties/{partyKey}. The server upserts the CartParties
      // row, recomposes peopleJson, and returns the fresh cart, so the old
      // expectedVersion conflict-retry/merge machinery is no longer needed.
      setSaving(true);
      setSaveError(null);
      const signal = makeSignal();
      try {
        const res = idx >= 0
          ? await client.retail.carts.updateParty(
              cart.cartId,
              personId,
              { person: withId as unknown as Record<string, unknown> },
              { signal },
            )
          : await client.retail.carts.addParty(
              cart.cartId,
              { clientId: personId, person: withId as unknown as Record<string, unknown> },
              { signal },
            );
        if (!res.success || !res.cart) {
          setSaveError(res.error ?? 'Save failed');
        } else {
          // BACKLOG-0721b kept — mirror business-entity beneficial owners into
          // metadataJson.beneficialOwners after the party command succeeds.
          onCartUpdated?.(await syncBoMirror(res.cart, signal));
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') {
          // navigated away; nothing to report
        } else if (idx < 0 && people.length === 0 && isClearedCartError(err)) {
          await recoverClearedCart(withId, personId, signal);
        } else {
          setSaveError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        setSaving(false);
      }
      } finally {
        inFlightSaveKeyRef.current = null;
      }
    },
    [cart.cartId, client, emit, makeSignal, onCartUpdated, people, syncBoMirror, editingPersonId, recoverClearedCart],
  );

  /**
   * no-dup-CIF guard (Bill 2026-06-02, person-fix 4). The dialog/shell save
   * path runs through here FIRST. For a fresh "+ New Person" (no cifId, not an
   * edit, not a review-add), run a SILENT CustSrch on the entered SSN (strong)
   * or name+DOB (tightened fallback) and, if an on-file CIF that's not already
   * in the cart matches, hold the append + open the CifCompareModal. Every
   * non-fresh-new save (edit / review-add of an on-file CIF) skips straight to
   * onModalSave. The guard is fail-open: any search error just adds (no block).
   */
  // Person save in flight (Bill 2026-10-06): the person modal closes at Save, but the Core
  // Director compare, the change-of-contact confirmation (documentation + account waterfall)
  // and the cart write still follow. Until they finish a blocking overlay covers the whole
  // screen, so the officer cannot click Next / Return and leave with the change not landed.
  const [personSaveInFlight, setPersonSaveInFlight] = useState<boolean>(false);

  const guardThenSave = useCallback(
    async (saved: RaoPerson): Promise<void> => {
      setPersonSaveInFlight(true);
      try {
        await guardThenSaveInnerRef.current?.(saved);
      } finally {
        setPersonSaveInFlight(false);
      }
    },
    [],
  );
  // Always the latest guardThenSaveInner (it closes over cart / editing state).
  const guardThenSaveInnerRef = useRef<((saved: RaoPerson) => Promise<void>) | null>(null);

  const guardThenSaveInner = useCallback(
    async (saved: RaoPerson): Promise<void> => {
      const isFreshNew = !saved.cifId && !editingPersonId && saved.mode !== 'existing';
      if (!isFreshNew) {
        // BACKLOG-1675 — existing customer: Core Director first (diff → confirm →
        // CustMod + account waterfall + notes), then the cart row. The officer can
        // back out of the Core write and keep the edits on the application only;
        // the banner says so, and the commit-time CIF update still runs for them.
        let toSave = saved;
        if (saved.cifId && isExistingCustomer(saved)) {
          const outcome = await beginProfileChange(saved, { source: 'rao-person-dialog', cartId: cart.cartId, surface: 'cart' });
          setProfileNotice(outcome.notice ?? null);
          if (!outcome.proceed) return;
          toSave = outcome.person;
        } else {
          setProfileNotice(null);
        }
        await onModalSave(toSave);
        return;
      }
      const ssn = (saved.taxId ?? '').replace(/\D/g, '');
      const useSsn = ssn.length === 9;
      const nm = `${saved.firstName ?? ''} ${saved.lastName ?? ''}`.trim();
      // Not enough identity to dedup on (no SSN + incomplete name) → just add.
      if (!useSsn && (!saved.firstName || !saved.lastName)) {
        await onModalSave(saved);
        return;
      }
      setCheckingDup(true);
      try {
        const raw = (await runCustomerSearch(client, useSsn ? ssn : nm, { signal: makeSignal() }))
          .filter((r) => !cartCifIds.includes(r.cifId));
        // Bill 2026-06-12 (answer #2): tighten the SSN-less fallback from full-
        // name-only to NAME + DOB. A name-match whose on-file DOB is KNOWN and
        // DIFFERENT no longer prompts; unknown-DOB rows still surface (the
        // compare modal hydrates the on-file DOB for the FSR to confirm).
        const matches = useSsn ? raw : filterByNameAndDob(raw, saved.dob);
        if (matches.length > 0) {
          setDupGuard({
            person: saved,
            matches,
            matchedOn: useSsn ? 'tax ID' : (saved.dob ? `name + DOB (${nm})` : `the name “${nm}”`),
            matchedOnTaxId: useSsn,
          });
          return; // wait for the officer's decision — do NOT append yet
        }
        await onModalSave(saved);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        await onModalSave(saved); // fail-open — a search hiccup must not block adding
      } finally {
        setCheckingDup(false);
      }
    },
    [client, makeSignal, cartCifIds, editingPersonId, onModalSave, beginProfileChange, cart.cartId],
  );
  guardThenSaveInnerRef.current = guardThenSaveInner;

  /**
   * E1 (Bill 2026-06-03; name+DOB trigger 2026-06-12) — EARLY dup-CIF check.
   * Fired from the open person modal when a complete 9-digit SSN is entered OR
   * (new) when both name parts + a DOB are entered on a fresh-new person (via
   * IndividualReviewBody → PdwShell.onEarlyDupCheck), so the officer learns of
   * an on-file match up front instead of after filling the whole form and
   * hitting Add. Opens the same CifCompareModal surface + resolve handlers as
   * the at-Add backstop (guardThenSave); the `early` flag only changes what
   * "not a match" does (dismiss/keep editing — clearing a colliding SSN — vs
   * save). Fail-open: any search error is swallowed so it never blocks entry.
   */
  const onEarlyDupCheck = useCallback(
    async (draft: RaoPerson, markNoTinCleared?: (key: string) => void): Promise<void> => {
      if (draft.cifId) return;                 // already an on-file CIF
      const ssn = (draft.taxId ?? '').replace(/\D/g, '');
      const dob = (draft.dob ?? '').replace(/\D/g, '');
      const nm = `${draft.firstName ?? ''} ${draft.lastName ?? ''}`.trim();
      // Two early triggers (Bill 2026-06-12, answer #2):
      //   (a) the existing 9-digit-SSN trigger (tax-id collision), and
      //   (b) a NEW name+DOB trigger once both first/last name AND a DOB are
      //       entered on a fresh-new person. Keyed independently so each fires
      //       at most once per distinct value this open-modal session.
      const useSsn = ssn.length === 9;
      const useNameDob = !useSsn && !!draft.firstName && !!draft.lastName && dob.length >= 8;
      // (c) Bill 2026-10-14 — a business / trust with "No EIN / TIN": search Core by the entity name.
      const entityName = (draft.name2 ?? '').trim();
      const useEntity = !useSsn && !useNameDob && draft.tinCode === 'N' && !draft.firstName && !draft.lastName && entityName.length > 0;
      if (!useSsn && !useNameDob && !useEntity) return;
      const key = useSsn ? `ssn:${ssn}` : useEntity ? `ne:${entityName.toLowerCase()}` : `nd:${nm.toLowerCase()}|${dob}`;
      if (lastEarlyDupSsn.current === key) return; // already prompted for this value
      lastEarlyDupSsn.current = key;
      setCheckingDup(true);
      try {
        const raw = (await runCustomerSearch(client, useSsn ? ssn : useEntity ? entityName : nm, { signal: makeSignal() }))
          .filter((r) => !cartCifIds.includes(r.cifId));
        const norm = (v: string | undefined): string => (v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
        const matches = useSsn
          ? raw
          : useEntity
            ? raw.filter((r) => norm(r.name) === norm(entityName))
            : filterByNameAndDob(raw, draft.dob);
        // No-TIN proof (Bill 2026-10-14): a completed name+DOB search (an entity: name search) with no
        // match is what lets TIN code N / Forn stand in for the tax ID. A search error stamps nothing.
        if ((useNameDob || useEntity) && matches.length === 0) markNoTinCleared?.(noTinIdentityKey(draft));
        if (matches.length > 0) {
          setDupGuard({
            person: draft,
            matches,
            matchedOn: useSsn ? 'tax ID' : useEntity ? `name (${entityName})` : `name + DOB (${nm})`,
            matchedOnTaxId: useSsn,
            early: true,
            ...((useNameDob || useEntity) && markNoTinCleared ? { markNoTinCleared } : {}),
          });
        }
      } catch {
        /* fail-open — an early-check hiccup must never block data entry */
      } finally {
        setCheckingDup(false);
      }
    },
    [client, makeSignal, cartCifIds],
  );

  /**
   * Compare modal — "Use existing CIF". The modal hands back the on-file person
   * hydrated + the chosen contact updates + every session artifact carried over
   * (the data-loss fix: the prior onDupReviewExisting discarded dupGuard.person).
   * We just open the review-add ceremony seeded with that merged person.
   */
  const onCompareUseExisting = useCallback(
    (person: RaoPerson): void => {
      setDupGuard(null);
      onSearchSelect(person); // Personal → PdwShell review-add / other → PersonDialog
    },
    [onSearchSelect],
  );

  /**
   * Compare modal — "Not a match — continue adding as new".
   *
   * HARD CAVEAT (Core DB integrity, Bill 2026-06-12): a new CIF may NEVER be
   * added with a tax id that matches an existing CIF (the CIF number is a Core
   * System key). When the duplicate was matched ON the tax id, FORCE-CLEAR the
   * entered SSN/EIN here — the person re-opens for the FSR to enter a corrected,
   * non-colliding value (re-add is blocked until then because guardThenSave
   * re-runs with no SSN → name+DOB only). Name+DOB-only matches (no tax-id
   * collision) keep the plain continue path (tax id untouched).
   *
   * Audit (answer #3): the rejected-match decision is recorded as a SessionEvent
   * (existing useEmitEvent machinery — no new SQL table).
   */
  const onCompareNotAMatch = useCallback(
    (candidate: CustomerSearchRow): void => {
      const g = dupGuard;
      setDupGuard(null);
      if (!g) return;
      // SessionEvent audit — officer (added by useEmitEvent from auth) +
      // timestamp (added by useEmitEvent) + candidate CIF + match basis.
      emit({
        eventType: 'cart.people.dup-cif-rejected',
        eventCategory: 'cart-lifecycle',
        severity: 'info',
        cartId: cart.cartId,
        payload: {
          candidateCifId: candidate.cifId,
          candidateName: candidate.name,
          matchedOn: g.matchedOn,
          matchedOnTaxId: g.matchedOnTaxId,
          pendingName: g.person.fullName
            || `${g.person.firstName ?? ''} ${g.person.lastName ?? ''}`.trim(),
          early: !!g.early,
        },
      });
      // Force-clear the entered tax id when the match was on it (caveat).
      const cleared: RaoPerson = g.matchedOnTaxId
        ? { ...g.person, taxId: '' }
        : g.person;
      // Early check: draft isn't finished — keep editing the open modal (with a
      // cleared SSN when colliding). The at-Add backstop re-checks on save, and
      // with the SSN cleared it can't re-collide on the same tax id. For the
      // at-Add check the person IS complete → create the new CIF directly.
      if (g.early) {
        // Re-seed the open shell draft so the cleared SSN takes effect. The
        // shell reads entryPerson; re-open it in review-add with the cleared
        // person so the FSR lands back on the form to correct the tax id.
        if (g.matchedOnTaxId) {
          setShellEntryPerson(cleared);
          setShellEntryMode('edit');
          setShellOpen(true);
        }
        // The officer looked at the name+DOB match and said it is someone else: that is the
        // no-TIN proof for this identity (Bill 2026-10-14).
        if (!g.matchedOnTaxId && g.markNoTinCleared) g.markNoTinCleared(noTinIdentityKey(g.person));
        // Non-colliding early reject: nothing to clear — the modal closes and
        // the FSR keeps editing the already-open shell.
        return;
      }
      void onModalSave(cleared);
    },
    [dupGuard, emit, cart.cartId, onModalSave],
  );

  /** Compare modal: back out — nothing added (officer can re-open "+ New Person"). */
  const onCompareCancel = useCallback((): void => {
    setDupGuard(null);
  }, []);

  return (
    <section aria-labelledby="rao-people-h">
      <h2 id="rao-people-h" style={{ fontSize: 18, color: 'var(--pcb-blue-dark)' }}>
        People on this application
      </h2>
      <p style={{ fontSize: 12, color: 'var(--ink-600)', marginTop: 'var(--sp-2)' }}>
        Add every person who will be on this account — owners, beneficiaries,
        signers, custodians. Each person’s role on each account is set on the Products step.
        {checkingDup ? ' · Checking for existing customer…' : comparingProfile ? ' · Comparing with Core Director…' : saving ? ' · Saving…' : ''}
      </p>

      {cartRecoveredNotice && (
        <div style={{ marginTop: 'var(--sp-5)' }}>
          <Banner tone="info">{cartRecoveredNotice}</Banner>
        </div>
      )}
      {saveError && (
        <div style={{ marginTop: 'var(--sp-5)' }}>
          <Banner tone="err">{saveError}</Banner>
        </div>
      )}
      {profileNotice && (
        <div style={{ marginTop: 'var(--sp-5)' }}>
          <Banner tone={profileNotice.tone}>{profileNotice.text}</Banner>
        </div>
      )}

      {!readOnly && (
        <div style={{ marginTop: 'var(--sp-7)' }}>
          <PersonSearchBar
            persistScope={cart.cartId}
            onSelectExisting={onSearchSelect}
            onAddNew={onAddNew}
            cartCifIds={cartCifIds}
            pendingRole={pendingRole}
          />
        </div>
      )}

      <div style={{ marginTop: 'var(--sp-7)' }}>
        <PeopleList
          people={people}
          {...(readOnly ? {} : { onEditPerson, onRemovePerson })}
          disabled={readOnly}
          {...(lockedPersonIds ? { lockedIds: lockedPersonIds } : {})}
        />
      </div>

      {/* Bill 2026-05-26 (v0.14.48, BACKLOG-0070) — PdwShell owns the "+ New
          Person" entry path end-to-end for Personal cohort. Stages 1-9
          render inside one persistent modal (NO left rail, NO 3-stage
          ribbon — per the canonical SPEC + feedback memos). Non-Personal
          buckets dispatch back through PersonDialog (legacy) at Stage 3
          confirm; those buckets get migrated into the shell in 0071+. */}
      <PdwShell
        open={shellOpen}
        onClose={() => { setShellOpen(false); setShellEntryPerson(null); }}
        cartId={cart.cartId}
        cartCifIds={cartCifIds}
        {...(cart.mode ? { cartMode: cart.mode } : {})}
        onNonPersonalDispatch={onNonPersonalDispatch}
        onSave={onShellSave}
        onEarlyDupCheck={onEarlyDupCheck}
        {...(shellEntryPerson ? { entryPerson: shellEntryPerson, entryMode: shellEntryMode } : {})}
      />

      {modalPerson && (
        <PersonDialog
          open={!!modalPerson}
          mode={modalMode}
          person={modalPerson}
          onSave={(p) => { void guardThenSave(p); }}
          onClose={() => setModalPerson(null)}
          cartCifIds={cartCifIds}
          cartId={cart.cartId}
          onEarlyDupCheck={onEarlyDupCheck}
        />
      )}

      {profileChangeModal}

      {personSaveInFlight && !profileChangeModal && !dupGuard && (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-live="assertive"
          aria-label="Saving the person"
          style={{
            position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(15, 23, 42, 0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <div
            style={{
              background: 'var(--surface-card)', borderRadius: 12, padding: '24px 32px', minWidth: 320, maxWidth: 440,
              boxShadow: '0 12px 40px rgba(0,0,0,0.25)', textAlign: 'center',
            }}
          >
            <div className="rao-busy-spinner" aria-hidden style={{
              width: 36, height: 36, margin: '0 auto 14px', borderRadius: '50%',
              border: '4px solid var(--surface-border)', borderTopColor: 'var(--pcb-blue, #2968ad)',
              animation: 'rao-busy-spin 0.9s linear infinite',
            }} />
            <style>{'@keyframes rao-busy-spin { to { transform: rotate(360deg); } }'}</style>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--pcb-blue-dark)' }}>
              {comparingProfile
                ? 'Checking for changes with Core Director…'
                : checkingDup
                  ? 'Checking for an existing customer…'
                  : 'Saving the person…'}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-600, #475569)', marginTop: 6 }}>
              Please wait — if anything changed, you'll be asked to confirm it before you continue.
            </div>
          </div>
        </div>
      )}

      {dupGuard && (
        <CifCompareModal
          open={!!dupGuard}
          cartId={cart.cartId}
          draft={dupGuard.person}
          matches={dupGuard.matches}
          matchedOn={dupGuard.matchedOn}
          matchedOnTaxId={dupGuard.matchedOnTaxId}
          onUseExisting={onCompareUseExisting}
          onNotAMatch={onCompareNotAMatch}
          onCancel={onCompareCancel}
        />
      )}
    </section>
  );
}
